import type { content as en } from "../en/content";
import type { Messages } from "../types";

export const content: Messages<typeof en> = {
  calendar: {
    title: "تقويم المحتوى",
    description: "خطّط وجدول وأعد جدولة المنشورات على كل الحسابات. اسحب المنشور لتحريكه.",
    month: "شهر",
    week: "أسبوع",
    agenda: "قائمة",
    today: "اليوم",
    prev: "السابق",
    next: "التالي",
    newPost: "إنشاء منشور",
    allAccounts: "كل الحسابات",
    more: "+{{n}} أخرى",
    empty: "لا شيء مجدول في هذه الفترة",
    emptyHint: "خطّط منشورك القادم: اختر موعداً أو دع ملفك يقترح أفضل وقت.",
    bestTime: "أفضل وقت",
    bestTimeHint: "جمهورك أكثر نشاطاً في هذه الساعة",
    dropHere: "أفلت لإعادة الجدولة",
    rescheduled: "أُعيدت الجدولة إلى {{when}}",
    rescheduleFailed: "تعذّرت إعادة جدولة المنشور",
    cantMovePublished: "لا يمكن نقل منشور تم نشره",
    cantMovePast: "اختر وقتاً في المستقبل",
    addAt: "إنشاء منشور في {{when}}",
    legend: "دليل الألوان",
    noAgenda: "لا منشورات قادمة",
    upcoming: "القادمة",
    past: "السابقة",
  },
  status: {
    draft: "مسودة",
    scheduled: "مجدول",
    published: "منشور",
    failed: "فشل",
    publishing: "جارٍ النشر",
    in_review: "قيد المراجعة",
  },
  composer: {
    title: "إنشاء منشور",
    description: "اكتب مرة واحدة وانشر على كل الحسابات، مع تعديلات لكل حساب.",
    targets: "النشر على",
    targetsHint: "اختر حساباً واحداً أو أكثر.",
    noTargets: "اختر حساباً واحداً على الأقل",
    quota: "{{used}} من {{quota}} منشور عبر الواجهة خلال آخر 24 ساعة",
    unsupportedType: "لا يدعم {{account}} هذا النوع",
    type: "نوع المنشور",
    media: "الوسائط",
    mediaHint: "أفلت الصور أو الفيديو هنا، أو تصفّح. حتى {{max}} عناصر.",
    browse: "تصفّح الملفات",
    library: "مكتبة الوسائط",
    libraryEmpty: "مكتبتك فارغة. ارفع ملفات أو ولّد صوراً في استوديو الذكاء.",
    uploading: "جارٍ الرفع…",
    uploadFailed: "فشل الرفع",
    removeMedia: "إزالة",
    reorderHint: "اسحب لإعادة ترتيب الألبوم",
    tooMany: "الألبوم يتسع حتى {{max}} عناصر",
    needMedia: "أضف صورة أو فيديو واحداً على الأقل",
    crop: "القص",
    caption: "النص",
    captionPlaceholder: "اكتب نص المنشور… ابدأ بجملة جاذبة واختم بدعوة لاتخاذ إجراء.",
    override: "نص مخصص لـ ‎@{{account}}",
    overrideHint: "اتركه فارغاً لاستخدام النص الرئيسي.",
    mainCaption: "الرئيسي",
    chars: "{{n}} / 2,200 حرف",
    hashtags: "{{n}} / 30 وسم",
    mentions: "{{n}} / 20 إشارة",
    ai: "اكتب بالذكاء الاصطناعي",
    aiBrief: "عن ماذا المنشور؟",
    aiBriefPlaceholder: "مثلاً: لوحة خريفية جديدة، 90×120، قطعة واحدة فقط",
    tone: "الأسلوب",
    tones: { casual: "عفوي", professional: "احترافي", playful: "مرح", luxury: "فاخر", bold: "جريء" },
    language: "اللغة",
    withHashtags: "أضف وسوماً",
    generate: "ولّد النصوص",
    generating: "جارٍ الكتابة…",
    useThis: "استخدم هذا",
    when: "الموعد",
    publishNow: "انشر الآن",
    schedule: "جدولة",
    date: "التاريخ",
    time: "الوقت",
    timezone: "الأوقات بتوقيت {{tz}}",
    bestTimes: "أوقات مقترحة",
    inPast: "هذا الوقت قد مضى",
    submitNow: "انشر الآن",
    submitSchedule: "جدولة المنشور",
    created: "تمت جدولة المنشور في {{when}}",
    publishing: "جارٍ النشر على {{n}} حساب…",
    createFailed: "تعذّر إنشاء المنشور",
    draftSaved: "تم حفظ المسودة على هذا الجهاز",
    draftRestored: "تمت استعادة المسودة",
    discard: "تجاهل المسودة",
    preview: "معاينة",
    previewFor: "معاينة كـ ‎@{{account}}",
    more: "المزيد",
    likedBy: "أعجب به متابعوك",
    viewComments: "عرض كل التعليقات",
    justNow: "الآن",
    checklist: "جاهز للنشر",
  },
  crops: { square: "مربع 1:1", portrait: "طولي 4:5", landscape: "عرضي 1.91:1", vertical: "عمودي 9:16" },
  score: {
    title: "تقييم المنشور",
    excellent: "ممتاز",
    good: "جيد",
    fair: "يحتاج تحسيناً",
    poor: "ضعيف",
    empty: "اكتب نصاً لترى تقييمه",
    fix: "إصلاح",
    checks: {
      hook: "ابدأ بجملة جاذبة قصيرة: السطر الأول يظهر قبل «المزيد» (125 حرفاً كحد أقصى).",
      length: "الطول المثالي بين 60 و1300 حرف.",
      cta: "أضف دعوة لاتخاذ إجراء: احفظ، علّق، راسلنا، أو الرابط في البايو.",
      hashtags: "استخدم من 3 إلى 15 وسماً مناسباً.",
      engagement: "اطرح سؤالاً لتشجيع التعليقات.",
      readability: "قسّم النص الطويل إلى فقرات قصيرة.",
      emoji: "رمز تعبيري أو اثنان يضيفان دفئاً.",
      limits: "التزم بحدود إنستغرام (2200 حرف، 30 وسماً، 20 إشارة).",
      reel: "في الريلز: اجعل النص قصيراً.",
    },
    fixes: {
      cta: "احفظ المنشور لترجع له 📌",
      engagement: "شو رأيكم؟ 👇",
      hashtags: "#فن #ديكور #لوحات",
      emoji: "✨",
    },
  },
  posts: {
    title: "المنشورات",
    description: "كل ما نشرته أو جدولته أو حفظته كمسودة، على كل الحسابات.",
    grid: "شبكة",
    list: "قائمة",
    account: "الحساب",
    status: "الحالة",
    allStatuses: "كل الحالات",
    empty: "لا منشورات بعد",
    emptyHint: "أنشئ منشورك الأول أو اربط حساباً لاستيراد المنشورات السابقة.",
    imported: "مستورد",
    caption: "النص",
    date: "التاريخ",
    reach: "الوصول",
    likes: "الإعجابات",
    comments: "التعليقات",
    loadMore: "تحميل المزيد",
  },
  detail: {
    back: "كل المنشورات",
    publications: "النشر على الحسابات",
    noMetrics: "تظهر الأرقام بعد النشر",
    comments: "التعليقات",
    noComments: "لا تعليقات بعد",
    rules: "القواعد على هذا المنشور",
    noRules: "تنطبق القواعد العامة للحساب فقط على هذا المنشور",
    allAccounts: "كل الحسابات",
    thisPost: "هذا المنشور",
    reschedule: "إعادة جدولة",
    cancel: "إلغاء المنشور",
    cancelConfirm: "إلغاء هذا المنشور المجدول؟",
    cancelled: "تم إلغاء المنشور",
    openOnInstagram: "فتح في إنستغرام",
    error: "خطأ",
    hidden: "مخفي",
    reply: "رد الحساب",
    scheduledFor: "مجدول في {{when}}",
    publishedAt: "نُشر {{when}}",
    createdBy: "أُنشئ في منصة ملفك",
    engagementRate: "معدل التفاعل",
  },
  /** ALP-84 S6: صفحة منشور أهدأ وقائمة المنشورات. */
  postPage: {
    untitled: "منشور بلا نص",
    tabs: { overview: "نظرة عامة", comments: "التعليقات", insights: "الأداء" },
    copyLink: "نسخ رابط إنستغرام",
    copied: "تم نسخ الرابط",
    cancelHint: "سيعود إلى المسودات ولن يُنشر.",
  },
  postsList: {
    search: "ابحث في نصوص المنشورات",
    more: "المزيد",
    reloadAllTitle: "إعادة تحميل كل المنشورات المستوردة من إنستغرام؟",
    reloadAllHint: "يعيد هذا قراءة حتى 500 منشور وتعليقاتها من إنستغرام. قد يستغرق بضع دقائق، ويمكنك متابعة العمل.",
    reloadAccountTitle: "إعادة تحميل منشورات @{{user}} من إنستغرام؟",
    noMatch: "لا توجد منشورات مطابقة",
    noMatchHint: "جرّب كلمة أو حسابًا أو حالة أخرى.",
  },
  composerCalm: {
    perAccount: "نص مختلف لكل حساب",
    discardTitle: "تجاهل هذه المسودة؟",
    discardHint: "سيُحذف النص والوسائط التي أضفتها هنا. لا يمكن التراجع عن ذلك.",
  },
  tagChips: { label: "التعليقات حسب الوسم", filter: "عرض {{tag}} فقط", clear: "عرض كل التعليقات (الحالي: {{tag}})" },
};
